import React, { FC, useEffect } from 'react';
import _ from 'lodash';
import { Box } from '@wix/design-system';
import FilterTags from './FilterTags';
import GalleryGrid from './GalleryGrid';
import {
  APP_STATUS,
  DEFAULT_FILTERS,
  useTemplatesContext,
  ACTIONS,
} from '@components/Contexts/TemplatesContext';
import PaginationFooter from '@components/PaginationFooter';
import { useTemplatesQuery } from '@components/Hooks/useTemplatesQuery';
import { useTemplateGalleryFilters } from '@components/Hooks/useTemplateGalleryFilters/useTemplateGalleryFilters';
import useTemplateSearchField from '@components/Templates/TemplateSearchField/useTemplateSearchField';
import { useTemplatesPaginationFooter } from '@components/PaginationFooter/useTemplatesPaginationFooter';
import { EmptyStateBannerWrapper } from '@components/Templates/EmptyStates/EmptyStateBannerWrapper';
import { EMPTY_STATE_BANNER_TYPES } from '@components/Templates/EmptyStates/EmptyStateBannerWrapper/EmptyStateBannerWrapper';
import { NUM_EMPTY_STATE_TEMPLATES_TO_DISPLAY } from '@utils/queryResponseUtils';
import TemplateGalleryErrorScreen from './TemplateGalleryErrorState';
import GalleryHeader from './GalleryHeader';
import TemplateCardListItems from './TemplateCardListItems';
import TemplateCardAdaptive from './TemplateCard/TemplateCardAdaptive';
import TemplateCardListItemsLoadingState from './TemplateCardListItems/TemplateCardListItemsLoadingState';
import TemplateGalleryEmptyFooter from './TemplateGalleryEmptyFooter';
import GalleryHeaderPopular from './GalleryHeader/GalleryHeaderPopular';
import PriceTaxExclusiveNotice from './PriceTaxExclusiveNotice';
import usePriceTaxExclusiveNotice from './PriceTaxExclusiveNotice/usePriceTaxExclusiveNotice';
import galleryGridStyle from './GalleryGrid/GalleryGrid.module.scss';
import s from './TemplateGallery.module.scss';

const TemplateGallery: FC = () => {
  const {
    state: { filters, currentPage, sort, appStatus, filterOpen, SSRFlow },
    dispatch,
  } = useTemplatesContext();
  const { isPriceTaxExclusiveNoticeShown } = usePriceTaxExclusiveNotice();

  const { isSearchEnabled } = useTemplateSearchField();
  const { hasActiveFilters, hasSearchTerm } = useTemplateGalleryFilters();

  const { data: userQueryData, isLoading } = useTemplatesQuery(
    filters,
    currentPage,
    sort,
  );
  const { data: emptyStateData } = useTemplatesQuery(
    DEFAULT_FILTERS,
    0,
    'purchases',
  );
  const { onPageClicked, getPageUrl, moreThanOnePage } =
    useTemplatesPaginationFooter({ queryData: userQueryData });

  const templateCount = userQueryData?.count;
  const hasNoTemplates = templateCount === 0;
  const noTemplatesForPage =
    _.size(userQueryData?.templates) <= 0 && (templateCount ?? 0 > 0);
  const showGeneralError = appStatus === APP_STATUS.ERROR || noTemplatesForPage;

  const showErrorScreens =
    (!isSearchEnabled && hasNoTemplates) || showGeneralError;
  const useSearchEmptyStates = hasNoTemplates && isSearchEnabled;
  const emptyStateBannerType = Boolean(filters?.search)
    ? EMPTY_STATE_BANNER_TYPES.search
    : EMPTY_STATE_BANNER_TYPES.filter;
  const showPaginationFooter = !hasNoTemplates && moreThanOnePage;

  const activeTemplateData = hasNoTemplates
    ? emptyStateData?.templates?.slice(0, NUM_EMPTY_STATE_TEMPLATES_TO_DISPLAY)
    : userQueryData?.templates;

  const hideSearchTermTag = templateCount === 0 || isLoading;

  const shouldNotPadGallery =
    hasActiveFilters || (hasSearchTerm && !hideSearchTermTag);

  const tagPaddingClassName = shouldNotPadGallery
    ? ''
    : isSearchEnabled
    ? s.noTagsPaddingLarger
    : s.noTagsPadding;

  useEffect(() => {
    window && window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [userQueryData]);

  useEffect(() => {
    dispatch({
      type: ACTIONS.SET_NUMBER_OF_RESULTS,
      payload: userQueryData?.count ?? 0,
    });
  }, [userQueryData?.count, dispatch]);

  return (
    <Box
      dataHook="templates-gallery-container"
      height="100%"
      direction="vertical"
      paddingBottom={isPriceTaxExclusiveNoticeShown ? '54px' : '152px'}
      borderLeft={'1px solid var(--wsr-color-D60, #DFE5EB)'}
    >
      <FilterTags hideSearchTermTag={hideSearchTermTag} />
      {showErrorScreens ? (
        <Box height="calc(100% - 171px)">
          <TemplateGalleryErrorScreen showEmptyError={hasNoTemplates} />
        </Box>
      ) : (
        <Box
          dataHook="templates-gallery"
          direction="vertical"
          minHeight="calc(100vh - 230px)"
          className={`${s.templateGalleryContainer} ${tagPaddingClassName}`}
        >
          {useSearchEmptyStates && (
            <EmptyStateBannerWrapper type={emptyStateBannerType} />
          )}
          <GalleryGrid
            header={
              useSearchEmptyStates ? (
                <GalleryHeaderPopular />
              ) : (
                <GalleryHeader templateCount={userQueryData?.count} />
              )
            }
            listClassName={filterOpen ? '' : galleryGridStyle.filterClosed}
            content={
              <TemplateCardListItems
                CardComponent={TemplateCardAdaptive}
                LoadingState={TemplateCardListItemsLoadingState}
                SSRFlow={SSRFlow}
                templates={activeTemplateData}
                isMobileView={false}
              />
            }
          />
          {
            <>
              {useSearchEmptyStates && <TemplateGalleryEmptyFooter />}
              {showPaginationFooter && (
                <PaginationFooter
                  currentPage={currentPage}
                  numberOfPages={userQueryData?.numberOfPages ?? 0}
                  getPageUrl={getPageUrl}
                  onPageClicked={onPageClicked}
                  showTopDivider={false}
                />
              )}
            </>
          }
          <PriceTaxExclusiveNotice
            shouldShowPriceNotice={isPriceTaxExclusiveNoticeShown}
          />
        </Box>
      )}
    </Box>
  );
};

export default TemplateGallery;
